import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { addItemsToFolders, removeItemsFromFolder, type FolderContentItem } from '@/api/queries/FolderQueries'
import { createDocument as apiCreateDocument } from '@/api/queries/DocumentQueries'
import { parseSelectedItems, type ShareTarget, type RenameTarget } from './UseContentManagerState'
import { CANVAS_ROOT_TYPES, type DocumentTemplateType, type CreateDocumentSectionRequest } from '@/types/blocks'
import type { ContentType } from '@/types/Content'
import type { ContentType as DbContentType } from '@/db/Types'

/**
 * Block operations interface from useContentData hook
 */
export interface BlockOperations {
	trash: (id: string) => Promise<void>
	restore: (id: string) => Promise<void>
	permanentDelete: (id: string) => Promise<void>
}

/**
 * Dependencies for the actions hook
 */
export interface UseContentManagerActionsParams {
	// Current state
	selectedItems: Set<string>
	selectedFolderId: string | null
	currentTeamId: string | null
	teams: Array<{ id: string }>

	// Data operations
	createPlaybook: (name: string, teamId: string | null) => Promise<{ id: string }>
	deletePlaybook: (id: string) => Promise<void>
	restore: (id: string) => Promise<void>
	permanentDelete: (id: string) => Promise<void>
	updatePlaybook: (id: string, data: { name: string }) => Promise<void>
	toggleStar: (id: string) => Promise<void>
	updateFolder: (id: string, name: string) => Promise<void>
	blockOperations: BlockOperations

	// State setters
	clearSelection: () => void
	setShareTarget: (target: ShareTarget | null) => void
	setRenameTarget: (target: RenameTarget | null) => void
	openModal: (name: string) => void

	// Content for looking up items
	filteredContent: Array<{
		id: string
		name: string
		type: ContentType
		teamId?: string | null
		sourcePlaybookId?: string
		sourcePlaybookName?: string
	}>
}

/**
 * Helper to check if a type is a block type
 */
function isBlockType(type: string): type is 'play' | 'formation' | 'concept' | 'document' {
	return [...CANVAS_ROOT_TYPES, 'document'].includes(type)
}

/**
 * Hook for managing ContentManager actions
 *
 * Consolidates all action handlers into a single hook.
 * Handles navigation, batch operations, and CRUD operations.
 */
export function useContentManagerActions({
	selectedItems,
	selectedFolderId,
	currentTeamId,
	teams,
	createPlaybook,
	deletePlaybook,
	restore,
	permanentDelete,
	updatePlaybook,
	toggleStar,
	updateFolder,
	blockOperations,
	clearSelection,
	setShareTarget,
	setRenameTarget,
	openModal,
	filteredContent,
}: UseContentManagerActionsParams) {
	const navigate = useNavigate()

	// ============================================================================
	// CREATE OPERATIONS
	// ============================================================================

	const handleCreatePlaybook = useCallback(async (name: string) => {
		const teamId = currentTeamId ?? teams[0]?.id ?? null
		const newPlaybook = await createPlaybook(name, teamId)
		navigate(`/playbooks/${newPlaybook.id}`)
	}, [currentTeamId, teams, createPlaybook, navigate])

	const handleCreateDocument = useCallback(async (
		playbookId: string,
		name: string,
		templateType: DocumentTemplateType,
		sections: CreateDocumentSectionRequest[],
		description?: string
	) => {
		const newDocument = await apiCreateDocument(playbookId, {
			name,
			template_type: 'call_sheet', // Default document format
			description,
			sections: sections?.map(s => ({ name: s.name, color: s.color })),
		})
		navigate(`/documents/${newDocument.id}/edit`)
	}, [navigate])

	// ============================================================================
	// SINGLE ITEM OPERATIONS
	// ============================================================================

	const handleDeleteItem = useCallback(async (item: { id: string; type: string }) => {
		if (isBlockType(item.type)) {
			await blockOperations.trash(item.id)
		} else {
			await deletePlaybook(item.id)
		}
	}, [deletePlaybook, blockOperations])

	const handlePermanentDeleteItem = useCallback(async (item: { id: string; type: string }) => {
		if (isBlockType(item.type)) {
			await blockOperations.permanentDelete(item.id)
		} else {
			await permanentDelete(item.id)
		}
	}, [permanentDelete, blockOperations])

	const handleRestoreItem = useCallback(async (item: { id: string; type: string }) => {
		if (isBlockType(item.type)) {
			await blockOperations.restore(item.id)
		} else {
			await restore(item.id)
		}
	}, [restore, blockOperations])

	const handleToggleStar = useCallback(async (id: string) => {
		await toggleStar(id)
	}, [toggleStar])

	const handleRenamePrompt = useCallback((id: string, currentName: string) => {
		setRenameTarget({
			id,
			name: currentName,
			type: 'playbook',
		})
		openModal('rename')
	}, [setRenameTarget, openModal])

	// ============================================================================
	// BATCH OPERATIONS
	// ============================================================================

	/**
	 * Generic batch action processor - handles batch operations across content types
	 */
	const processBatchAction = useCallback(async (
		playbookFn: (id: string) => Promise<unknown>,
		documentFn: (id: string) => Promise<unknown>,
		blockFn?: (id: string) => Promise<unknown>
	) => {
		const items = parseSelectedItems(selectedItems)

		await Promise.allSettled(items.map(item => {
			switch (item.type) {
				case 'playbook':
					return playbookFn(item.id)
				case 'document':
					return documentFn(item.id)
				case 'play':
				case 'formation':
				case 'concept':
					return blockFn ? blockFn(item.id) : Promise.resolve()
				default:
					return Promise.resolve()
			}
		}))

		clearSelection()
	}, [selectedItems, clearSelection])

	const handleBatchRestore = useCallback(async () => {
		await processBatchAction(
			restore,
			blockOperations.restore,
			blockOperations.restore
		)
	}, [processBatchAction, restore, blockOperations])

	const handleBatchPermanentDelete = useCallback(async () => {
		await processBatchAction(
			permanentDelete,
			blockOperations.permanentDelete,
			blockOperations.permanentDelete
		)
	}, [processBatchAction, permanentDelete, blockOperations])

	const handleBatchDelete = useCallback(async () => {
		await processBatchAction(
			deletePlaybook,
			blockOperations.trash,
			blockOperations.trash
		)
	}, [processBatchAction, deletePlaybook, blockOperations])

	// ============================================================================
	// FOLDER OPERATIONS
	// ============================================================================

	const handleAddToFolders = useCallback(async (
		folderIds: string[],
		items: FolderContentItem[]
	) => {
		await addItemsToFolders(folderIds, items)
		clearSelection()
	}, [clearSelection])

	const handleRemoveFromFolder = useCallback(async () => {
		if (selectedFolderId === null) return

		const items: FolderContentItem[] = parseSelectedItems(selectedItems)
			.filter(item => ['playbook', 'document'].includes(item.type))
			.map(item => ({
				type: item.type as DbContentType,
				id: item.id,
			}))

		await removeItemsFromFolder(selectedFolderId, items)
		clearSelection()
	}, [selectedFolderId, selectedItems, clearSelection])

	const handleRenameFolder = useCallback((folderId: string, currentName: string) => {
		setRenameTarget({
			id: folderId,
			name: currentName,
			type: 'folder',
		})
		openModal('rename')
	}, [setRenameTarget, openModal])

	// ============================================================================
	// SHARE OPERATION
	// ============================================================================

	const handleShare = useCallback((id: string) => {
		const item = filteredContent.find(i => i.id === id)
		if (!item) return

		if (item.type === 'playbook') {
			setShareTarget({
				id,
				name: item.name,
				teamId: item.teamId ?? null,
			})
			openModal('share')
		} else if (item.type === 'document' && item.sourcePlaybookId) {
			// Documents inherit sharing from parent playbook
			setShareTarget({
				id: item.sourcePlaybookId,
				name: item.sourcePlaybookName ?? 'Playbook',
				teamId: item.teamId ?? null,
			})
			openModal('share')
		}
	}, [filteredContent, setShareTarget, openModal])

	return {
		// Create operations
		handleCreatePlaybook,
		handleCreateDocument,

		// Single item operations
		handleDeleteItem,
		handlePermanentDeleteItem,
		handleRestoreItem,
		handleToggleStar,
		handleRenamePrompt,

		// Batch operations
		handleBatchRestore,
		handleBatchPermanentDelete,
		handleBatchDelete,

		// Folder operations
		handleAddToFolders,
		handleRemoveFromFolder,
		handleRenameFolder,

		// Share operation
		handleShare,
	}
}

export type UseContentManagerActionsReturn = ReturnType<typeof useContentManagerActions>
